import { ZonedDateTime } from '@internationalized/date';
import {
  DriverSortOption,
  DriverStatus,
} from '@rematter/frontend-lib-api-client';

import { DriverRecord } from '~/modules/transportation/types';
import { createQueryHook } from '~/shared/providers/api';
import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const useDriversQuery = createQueryHook(
  'transportation.drivers',
  (params: {
    ids?: string[];
    status?: DriverStatus;
    firstName?: string;
    lastName?: string;
    licenseNumber?: string;
    phoneNumber?: string;
    email?: string;
    lastUpdatedAt?: ZonedDateTime;
    searchTerm?: string;
    limit?: number;
    offset?: number;
    sorting?: DriverSortOption[];
    haulerId?: string;
  }) => ({
    drivers: {
      __args: {
        filter: {
          ids: params.ids,
          status: params.status ? [params.status] : undefined,
          firstName: params.firstName,
          lastName: params.lastName,
          licenseNumber: params.licenseNumber,
          phoneNumber: params.phoneNumber,
          email: params.email,
          lastUpdatedAt: params.lastUpdatedAt?.toAbsoluteString(),
          searchTerm: params.searchTerm,
          haulerId: params.haulerId,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        status: true,
        firstName: true,
        lastName: true,
        licenseNumber: true,
        licenseExpiration: true,
        phoneNumber: true,
        email: true,
        address: {
          __scalar: true,
        },
        user: {
          id: true,
        },
        defaultTruckId: true,
        defaultTrailerId: true,
        defaultTruck: {
          id: true,
          licensePlate: true,
        },
        defaultTrailer: {
          id: true,
          licensePlate: true,
        },
        currentTruck: {
          id: true,
          licensePlate: true,
        },
        currentTrailer: {
          id: true,
          licensePlate: true,
        },
        displayOrder: true,
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { drivers: DriverRecord[]; total?: number } => ({
      drivers: data?.drivers.entries.map((driver) => ({
        id: driver.id,
        status: driver.status,
        firstName: driver.firstName,
        lastName: driver.lastName,
        licenseNumber: driver.licenseNumber ?? undefined,
        licenseExpiration: maybeParseCalendarDate(driver.licenseExpiration),
        phoneNumber: driver.phoneNumber,
        email: driver.email,
        address: driver.address,
        user: driver.user ?? undefined,
        defaultTruckId: driver.defaultTruckId ?? undefined,
        defaultTrailerId: driver.defaultTrailerId ?? undefined,
        defaultTruck: driver.defaultTruck
          ? {
              id: driver.defaultTruck.id,
              licensePlate: driver.defaultTruck.licensePlate,
            }
          : undefined,
        defaultTrailer: driver.defaultTrailer
          ? {
              id: driver.defaultTrailer.id,
              licensePlate: driver.defaultTrailer.licensePlate,
            }
          : undefined,
        currentTruck: driver.currentTruck
          ? {
              id: driver.currentTruck.id,
              licensePlate: driver.currentTruck.licensePlate,
            }
          : undefined,
        currentTrailer: driver.currentTrailer
          ? {
              id: driver.currentTrailer.id,
              licensePlate: driver.currentTrailer.licensePlate,
            }
          : undefined,
        displayOrder: driver.displayOrder ?? undefined,
        timestamps: {
          createdAt: parseZonedDateTime(driver.timestamps?.createdAt),
          updatedAt: parseZonedDateTime(driver.timestamps?.updatedAt),
        },
      })),
      total: data?.drivers.page.total ?? undefined,
    }),
  },
);
